'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useMemo, useEffect } from 'react';
import { FeedContextProvider } from './Feed.Context';

const FeedPageWrapper = dynamic(
  () =>
    import('./Container.component').then((mod) => ({ default: mod.default })),
  {
    loading: () => null,
    ssr: true,
  },
);

// Prefetch the component after initial load to avoid blocking TBT
if (typeof window !== 'undefined') {
  // Use requestIdleCallback to defer until main thread is idle
  const prefetch = () => import('./Container.component');

  if ('requestIdleCallback' in window) {
    requestIdleCallback(prefetch, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(prefetch, 1000);
  }
}

import { IItem } from '@/types/feed.types';

interface ClientFeedWrapperProps {
  itemId: string;
  initialItem: IItem;
  isMobile: boolean;
}

export default function ClientFeedWrapper({
  itemId,
  initialItem,
  isMobile,
}: ClientFeedWrapperProps) {
  const searchParams = useSearchParams();

  const playlistId = searchParams.get('c') ?? undefined;
  const searchString = searchParams.get('q') ?? undefined;
  const collectionId = searchParams.get('f') ?? undefined;

  const contextValue = useMemo(() => {
    const shouldFetchItemsList = Boolean(
      playlistId || searchString || collectionId,
    );

    return {
      itemId,
      playlistId,
      searchString,
      collectionId,
      shouldFetchItemsList,
      itemDetails: initialItem,
    };
  }, [itemId, playlistId, searchString, collectionId, initialItem]);

  // Hide server skeleton immediately when this client component mounts
  useEffect(() => {
    const hideSkeleton = () => {
      const serverSkeleton = document.getElementById('server-feed-skeleton');
      if (serverSkeleton) {
        serverSkeleton.style.transition = 'opacity 0.2s ease-out';
        serverSkeleton.style.opacity = '0';
        setTimeout(() => {
          serverSkeleton.style.display = 'none';
        }, 200);
      }
    };

    // Execute immediately
    hideSkeleton();

    // Also try on next tick in case DOM isn't ready
    const timer = setTimeout(hideSkeleton, 10);

    return () => clearTimeout(timer);
  }, [itemId]); // Re-run when itemId changes (navigation)

  return (
    <FeedContextProvider contextValue={contextValue}>
      <FeedPageWrapper
        itemId={itemId}
        playlistId={playlistId}
        isMobile={isMobile}
        searchString={searchString}
        collectionId={collectionId}
        initialItem={initialItem}
      />
    </FeedContextProvider>
  );
}
